import { actionChannel, all, call, delay, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { constants } from 'config/constants';
import { settingsActions } from 'reduxStore/settings/slice';
import { camerasActions } from './slice';
import { selectConfiguredProviders } from './selectors';

import { Notification } from 'zComponents/atoms';

// Camera sagas: the list comes from /devices, playback from /cameras stream-token.
// Server error sentences are shown verbatim — they know the vendor's caps.

// Shared with the device drawer; also the allowlist for the provider segment in sync URLs.
const PROVIDER_LABELS = constants.CAMERA_PROVIDER_LABELS;

// 🛑 Call inside the `try`: the dynamic import rejects after a deploy, and outside it the
// loading flag is never cleared (surface stuck on a skeleton).
const getSuperFetch = function* () {
	const module = yield import('library/helpers/superFetch');
	return module.default;
};

// One still frame for a wall tile. Best-effort: a failure keeps the frame on screen, no toast.
export function* loadThumbnail({ payload }) {
	const { deviceID, resolution } = payload;
	try {
		const superFetch = yield* getSuperFetch();
		const res = yield superFetch.get(`/cameras/${deviceID}/thumbnail`, {}, resolution ? { resolution } : {});
		if (res.statusCode === 429) {
			yield put(camerasActions.loadThumbnailError({ deviceID, error: res.error, transient: true }));
			return;
		}
		if (res.statusCode !== 200) throw new Error(res.error);
		const { image, capturedAt } = res.data || {};
		yield put(camerasActions.loadThumbnailSuccess({ deviceID, image, capturedAt }));
	} catch (error) {
		yield put(camerasActions.loadThumbnailError({ deviceID, error: error.message }));
	}
}

// Stills asked at once; a whole wall in parallel tripped the server's rate limiter.
export const THUMBNAIL_CONCURRENCY = 4;

// Queues loadThumbnail and drains it with a fixed pool of workers, in wall order.
export function* thumbnailPool() {
	const queue = yield actionChannel(camerasActions.loadThumbnail);
	for (let i = 0; i < THUMBNAIL_CONCURRENCY; i += 1) {
		yield fork(function* thumbnailWorker() {
			while (true) {
				const action = yield take(queue);
				yield call(loadThumbnail, action);
			}
		});
	}
}

// Like takeEvery, but drops a repeat for a device whose request is still in flight.
export function* takeLeadingPerDevice(pattern, worker) {
	const inFlight = new Set();
	yield takeEvery(pattern, function* leadingPerDevice(action) {
		const { deviceID } = action.payload;
		if (inFlight.has(deviceID)) return;
		inFlight.add(deviceID);
		try {
			yield call(worker, action);
		} finally {
			inFlight.delete(deviceID);
		}
	});
}

// Does the ORG own any cameras (a boolean, not a list) — the empty state is org-wide while a
// site view holds one site. Asks only enabled providers; the flag it sets stops re-dispatch.
export function* probeOrgCameras() {
	const configured = yield select(selectConfiguredProviders);
	if (!configured.verkada && !configured.vortex) return;

	try {
		const superFetch = yield* getSuperFetch();
		// No `siteID`: the question is org-wide.
		const askFor = (defaultCategoryID) => call(() => superFetch.get('/devices', {}, { defaultCategoryID }));

		const [verkada, vortex] = yield all([
			configured.verkada ? askFor(constants.VERKADA_CAMERAS) : null,
			configured.vortex ? askFor(constants.VORTEX_CAMERAS) : null
		]);

		for (const res of [verkada, vortex]) {
			if (res && res.statusCode !== 200) throw new Error(res.error?.message || res.error);
		}

		const total = (verkada?.data || []).length + (vortex?.data || []).length;
		yield put(camerasActions.probeOrgCamerasResult({ orgHasCameras: total > 0 }));
	} catch (error) {
		// Quiet: a failed probe leaves `orgHasCameras` false, which the empty state handles.
	}
}

export function* loadEvents({ payload }) {
	const { deviceID, startDate, endDate, limit } = payload;
	try {
		const superFetch = yield* getSuperFetch();
		const res = yield superFetch.get(
			`/cameras/${deviceID}/events`,
			{},
			{ startDate, endDate, ...(limit ? { limit } : {}) }
		);
		if (res.statusCode !== 200) throw new Error(res.error);
		// ⚠️ `supported` defaults to TRUE; false would caption a working camera as "no events".
		const { events = [], supported = true, truncated = false } = res.data || {};
		yield put(camerasActions.loadEventsSuccess({ deviceID, startDate, endDate, events, supported, truncated }));
	} catch (error) {
		yield put(camerasActions.loadEventsError({ deviceID, startDate, endDate, error: error.message }));
	}
}

// Mints one live stream descriptor per camera; past windows rewrite its url client-side.
// Server-minted because Verkada 403s the browser's preflight on the token endpoint.
export function* loadStream({ payload }) {
	const { deviceID, resolution } = payload;
	try {
		const superFetch = yield* getSuperFetch();
		const res = yield superFetch.post(`/cameras/${deviceID}/stream-token`, resolution ? { resolution } : {});
		if (res.statusCode !== 200) throw new Error(res.error);

		const { expiresAt, ...descriptor } = res.data || {};
		yield put(
			camerasActions.loadStreamSuccess({
				deviceID,
				// Verkada: credential + ids; VORTEX: SDK inputs. resolvePlayback builds the url.
				...descriptor,
				// ms → seconds, the unit the slice, selectors and refresh timer share.
				...(expiresAt ? { expiresAt: Math.floor(expiresAt / 1000) } : {}),
				requestedAt: Math.floor(Date.now() / 1000)
			})
		);
	} catch (error) {
		// Shown in the viewport, not a toast: it is about the camera on screen.
		yield put(camerasActions.loadStreamError({ deviceID, error: error.message }));
	}
}

// Provider sync status. ⚠️ adminOnly: a site-scoped caller's 403 resolves to null (strip hides).
export function* loadSources() {
	try {
		const superFetch = yield* getSuperFetch();
		const [verkada, vortex] = yield all([
			call(() => superFetch.get('/verkada/sync/status').catch(() => null)),
			call(() => superFetch.get('/vortex/sync/status').catch(() => null))
		]);
		yield put(
			camerasActions.loadSourcesSuccess({
				verkada: verkada?.statusCode === 200 ? verkada.data : null,
				vortex: vortex?.statusCode === 200 ? vortex.data : null
			})
		);
	} catch (error) {
		yield put(camerasActions.loadSourcesError(error.message));
	}
}

// Starts a provider sync for both the Cameras page and Integrations settings.
// ⚠️ Both refreshes are needed: one page reads /sync/status, the other org setting 145.
export function* syncSource({ payload } = {}) {
	const provider = PROVIDER_LABELS[payload?.provider] ? payload.provider : 'verkada';
	const label = PROVIDER_LABELS[provider];

	try {
		const superFetch = yield* getSuperFetch();
		const res = yield superFetch.post(`/${provider}/sync`);
		if (res.statusCode !== 200) throw new Error(res.error);
		Notification('success', `${label} camera sync started.`);
		yield put(camerasActions.syncSourceSuccess({ provider }));
		yield put(camerasActions.loadSources());
		yield put(settingsActions.loadOrgSettings());
	} catch (error) {
		Notification('error', error.message || `Failed to start the ${label} sync.`);
		yield put(camerasActions.syncSourceError({ provider, error: error.message }));
	}
}

// Integrations settings: checks the provider's API key; the verify writes the connected
// state into org settings, so they are reloaded after.
export function* verifyConnection({ payload } = {}) {
	const provider = PROVIDER_LABELS[payload?.provider] ? payload.provider : 'verkada';
	const label = PROVIDER_LABELS[provider];

	try {
		const superFetch = yield* getSuperFetch();
		const res = yield superFetch.post(`/${provider}/verify`);
		if (res.statusCode !== 200) throw new Error(res.error);
		const { cameraCount } = res.data || {};
		Notification('success', `${label} connection verified — ${cameraCount ?? 0} cameras found.`);
		yield put(camerasActions.verifyConnectionSuccess(res.data));
		yield put(settingsActions.loadOrgSettings());
	} catch (error) {
		Notification('error', error.message || `Failed to verify the ${label} connection.`);
		yield put(camerasActions.verifyConnectionError({ error: error.message }));
	}
}

// ── Clips: a clip is a row; the POST answers at once and its status is polled (no socket).

// Asks for a window as an MP4. ⚠️ Answers 202 (queued), so success is `< 300`, not `=== 200`.
export function* requestClip({ payload }) {
	const { deviceID, startDate, endDate } = payload;
	try {
		const superFetch = yield* getSuperFetch();
		const res = yield superFetch.post(`/cameras/${deviceID}/clips`, { startDate, endDate });
		if (res.statusCode >= 300) throw new Error(res.error);
		yield put(camerasActions.requestClipSuccess({ clip: res.data }));
	} catch (error) {
		Notification('error', error.message || 'The clip could not be requested.');
		yield put(camerasActions.requestClipError({ deviceID, error: error.message }));
	}
}

// Every clip this caller can see on one camera, newest first.
export function* loadClips({ payload }) {
	const { deviceID } = payload;
	try {
		const superFetch = yield* getSuperFetch();
		const res = yield superFetch.get('/cameras/clips', {}, { deviceID });
		if (res.statusCode !== 200) throw new Error(res.error);
		yield put(camerasActions.loadClipsSuccess({ deviceID, clips: res.data || [] }));
	} catch (error) {
		// Quiet: the panel shows its own empty state rather than a toast about a list.
		yield put(camerasActions.loadClipsError({ deviceID, error: error.message }));
	}
}

// One status read for a clip still `processing`.
export function* pollClip({ payload }) {
	try {
		const superFetch = yield* getSuperFetch();
		const res = yield superFetch.get(`/cameras/clips/${payload.clipID}`);
		if (res.statusCode !== 200) throw new Error(res.error);
		yield put(camerasActions.pollClipSuccess({ clip: res.data }));
	} catch (error) {
		yield put(camerasActions.pollClipError({ clipID: payload.clipID, error: error.message }));
	}
}

// A clip is typically ready in 10–20 s.
const CLIP_POLL_MS = 2000;

// Three minutes of polling, then stop following (the clip stays in the camera's Clips tab).
const CLIP_POLL_MAX = 90;

// Follows a requested clip to its verdict as a background job (survives leaving the table).
// Ends on ready/failed, on dismissal (drops out of `watching`) or on CLIP_POLL_MAX.
export function* watchClip({ payload }) {
	const { clipID, status } = payload.clip || {};
	if (!clipID || status !== 'processing') return;

	for (let asked = 0; asked < CLIP_POLL_MAX; asked += 1) {
		yield delay(CLIP_POLL_MS);
		const watching = yield select((state) => state.Cameras.clips.watching);
		if (!watching.includes(clipID)) return;
		yield call(pollClip, { payload: { clipID } });
		const clip = yield select((state) => state.Cameras.clips.byID[clipID]);
		// Gone (deleted) or settled — either way there is nothing left to ask.
		if (!clip || clip.status !== 'processing') return;
	}

	yield put(camerasActions.stopWatchingClip({ clipID }));
}

// 🛑 A POST so the audit log records each download; the polled GET would drown it.
// The 5-minute URL lives only here — the slice never stores it.
export function* downloadClip({ payload }) {
	try {
		const superFetch = yield* getSuperFetch();
		const res = yield superFetch.post(`/cameras/clips/${payload.clipID}/download`, {});
		if (res.statusCode !== 200) throw new Error(res.error);
		const url = res.data?.downloadUrl;
		if (!url) throw new Error('That clip is not ready to download yet.');
		window.open(url, '_blank', 'noopener');
	} catch (error) {
		Notification('error', error.message || 'The clip could not be downloaded.');
	}
}

export function* deleteClip({ payload }) {
	const { clipID, deviceID } = payload;
	try {
		const superFetch = yield* getSuperFetch();
		const res = yield superFetch.delete(`/cameras/clips/${clipID}`, {});
		if (res.statusCode !== 200) throw new Error(res.error);
		yield put(camerasActions.deleteClipSuccess({ clipID, deviceID }));
	} catch (error) {
		Notification('error', error.message || 'The clip could not be removed.');
		yield put(camerasActions.deleteClipError({ clipID, error: error.message }));
	}
}

export default function* camerasSagas() {
	yield takeLatest(camerasActions.probeOrgCameras, probeOrgCameras);
	// takeEvery: the timeline and the history modal load different windows at once.
	yield takeEvery(camerasActions.loadEvents, loadEvents);
	// One mint per camera at a time: a second player or the refresh timer used to mint twice,
	// and the slower answer overwrote the newer token.
	yield fork(takeLeadingPerDevice, camerasActions.loadStream, loadStream);
	yield fork(thumbnailPool);
	yield takeLatest(camerasActions.loadSources, loadSources);
	yield takeEvery(camerasActions.syncSource, syncSource);
	yield takeLatest(camerasActions.verifyConnection, verifyConnection);
	// takeEvery per clip: cancelling one would strand its row in `processing`.
	yield takeEvery(camerasActions.requestClip, requestClip);
	yield takeEvery(camerasActions.pollClip, pollClip);
	yield takeEvery(camerasActions.requestClipSuccess, watchClip);
	yield takeEvery(camerasActions.downloadClip, downloadClip);
	yield takeEvery(camerasActions.deleteClip, deleteClip);
	// takeLatest: one camera is open, so an earlier camera's list must not land after it.
	yield takeLatest(camerasActions.loadClips, loadClips);
}
